/**
 * Filter Utilities
 *
 * Role-based filtering logic for OKR objectives
 */

import {
    UserContext,
    FilteredObjectives,
    OrgObjective,
    TeamObjective,
    IndividualObjective
} from '../types';

/**
 * Check if user is admin
 */
export const isUserAdmin = (userContext: UserContext): boolean => {
    return userContext.role === 'Admin' || userContext.role === 'Super Admin';
};

/**
 * Check if user is HR
 */
export const isUserHR = (userContext: UserContext): boolean => {
    return userContext.role === 'HR';
};

/**
 * Check if user is manager
 */
export const isUserManager = (userContext: UserContext): boolean => {
    return userContext.is_manager || (userContext.name?.toLowerCase().includes('manager'));
};

/**
 * Filter objectives based on user role
 */
export const filterObjectivesByRole = (
    userContext: UserContext,
    orgObjectives: OrgObjective[],
    teamObjectives: TeamObjective[],
    myObjectives: IndividualObjective[]
): FilteredObjectives => {
    const isAdmin = isUserAdmin(userContext);
    const isManager = isUserManager(userContext);
    const isHR = isUserHR(userContext);

    // Filter team objectives to only those linked to current period's org objectives
    const currentOrgIds = orgObjectives.map(o => o.id);
    const periodFilteredTeams = teamObjectives.filter(t => currentOrgIds.includes(t.org_objective_id));

    // Admin and HR see everything (filtered by period)
    if (isAdmin || isHR) {
        const myDeptTeam = (isManager && userContext.dept_id)
            ? periodFilteredTeams.filter(t => Number(t.department_id) === Number(userContext.dept_id))
            : undefined;
        return {
            org: orgObjectives,
            team: periodFilteredTeams,
            ...(myDeptTeam && { myDeptTeam }),
            individual: myObjectives
        };
    }

    // Manager sees only their department's team objectives + all individual objectives in their dept
    if (isManager && userContext.dept_id) {
        const deptTeamObjectives = periodFilteredTeams.filter(
            t => Number(t.department_id) === Number(userContext.dept_id)
        );
        // Manager can see all individual objectives linked to their department's team objectives
        const deptIndividualObjectives = myObjectives.filter(obj => {
            const linkedTeam = deptTeamObjectives.find(t => t.id === obj.team_objective_id);
            return linkedTeam !== undefined;
        });

        return {
            org: orgObjectives, // Everyone sees org objectives
            team: deptTeamObjectives,
            individual: deptIndividualObjectives
        };
    }

    // Employee sees their own objectives AND objectives with milestones assigned to them
    const myIndividual = myObjectives.filter(
        obj => obj.employee_id === userContext.id ||
               ((obj as any).milestones && (obj as any).milestones.some((m: any) => m.assigned_to === userContext.id))
    );

    // Show ALL team objectives for their department so they can see assigned tasks
    const myTeam = periodFilteredTeams.filter(
        t => Number(t.department_id) === Number(userContext.dept_id)
    );

    return {
        org: orgObjectives, // Everyone sees org objectives
        team: myTeam,
        individual: myIndividual
    };
};

/**
 * Calculate team contributions for display
 */
export interface TeamContribution {
    id: number;
    objectiveId: string | null;
    name: string;
    weight: number;
    score: number;
    status: string;
    memberCount?: number;
}

/**
 * Compute display status from score percentage
 * Used for Strategic Units view where status should reflect actual progress
 */
const getDisplayStatusFromScore = (score: number): string => {
    if (score >= 100) return 'Completed';
    if (score >= 75) return 'Almost Complete';
    if (score >= 50) return 'On Track';
    if (score >= 25) return 'In Progress';
    if (score > 0) return 'Started';
    return 'Not Started';
};

export const calculateTeamContributions = (
    departments: any[],
    teamObjectives: TeamObjective[]
): TeamContribution[] => {
    return departments.map(dept => {
        // Handle both API formats: dept_id/dept_name (from backend) and id/name (normalized)
        const deptId = dept.dept_id ?? dept.id;
        const deptName = dept.dept_name ?? dept.name;

        const deptObjective = teamObjectives.find(
            t => Number(t.department_id) === Number(deptId)
        );

        // For Strategic Units view, derive status from score for meaningful display
        const score = deptObjective?.score || 0;
        const displayStatus = getDisplayStatusFromScore(score);

        return {
            id: deptId,
            objectiveId: deptObjective?.id || null,
            name: deptName,
            weight: deptObjective?.weight || 0,
            score: score,
            status: displayStatus,
            memberCount: dept.memberCount ?? dept.member_count ?? 0
        };
    });
};
